import axios from "axios";
import { skipWhile, take } from "rxjs";
import { createAction } from "@reduxjs/toolkit";

import type { AsyncTaskStatus, AsyncTaskType } from "../inheritFromWeeb-ui-v2/generated/graphql";
import { getTheirStorySdk } from "../inheritFromWeeb-ui-v2/state/gqlClient";
import type { ActionTypeHelper } from "../rootStore";
import {
  STORIES_LOAD_FAILED,
  STORIES_LOADED,
  STORIES_LOADING,
  STORIES_ONE_UPDATED,
  STORIES_SET_PAGE_VIEW,
  STORIES_CLEAR,
  STORY_TASK_UPDATED,
  STORIES_SORT_SETTING,
} from "../../actions/types";

import type { AsyncTaskId, StoryId } from "./reducer";

export const loadStoriesMyInterview =
  (params: any = {}) =>
  async (dispatch, getState) => {
    try {
      const {
        stories: {
          groupId: currentGroupId,
          paging: { size: pageSize, current: currentPage },
        },
      } = getState();

      const { groupId, clear } = params;
      const newGroupId = groupId !== undefined ? groupId : currentGroupId;

      // We put an skeleton loader when we are loading the first page, after that is handled by the video card list
      if (currentPage === 1) {
        dispatch({ type: STORIES_LOADING, payload: { clear: clear || currentGroupId !== newGroupId } });
      }

      const urlPath = newGroupId == null ? "/stories" : `/groups/${newGroupId}/stories`;
      const searchParam = window.location.search;
      const urlParams = new URLSearchParams(searchParam);

      const search = urlParams.get("search");
      const transcriptSearch = urlParams.get("transcriptSearch");

      let searchQuery = "";
      if (search) {
        searchQuery = `?search=${search}`;
      } else if (transcriptSearch) {
        searchQuery = `?search=${transcriptSearch}`;
      }

      const res = await axios.get(`${urlPath}/${searchQuery}`, {
        params: { pageSize, page: currentPage },
      });

      const { items: newStories, total, allStoriesArrayTranscripts } = res.data;

      const stories = [...getState().stories.stories, ...newStories] ?? [];

      let page = currentPage;

      if (stories.length < total) {
        page = currentPage + 1;
      }

      dispatch({
        type: STORIES_LOADED,
        payload: { stories, total, page, groupId: newGroupId, allStoriesArrayTranscripts },
      });
      return stories;
    } catch (err) {
      dispatch({ type: STORIES_LOAD_FAILED });
      throw err;
    }
  };

export const setStoriesPageView =
  ({ currentPage, pageSize }: any) =>
  async (dispatch) => {
    dispatch({ type: STORIES_SET_PAGE_VIEW, payload: { currentPage, pageSize } });
    dispatch(loadStoriesMyInterview());
  };

export const setStoriesSort = (column, order) => async (dispatch) => {
  dispatch({ type: STORIES_SORT_SETTING, payload: { column, order } });
};

export const togglePublishToGroup = (story, groupId) => async (dispatch) => {
  const isAdd = !story.groups.some((g) => g.group === groupId);
  const params = isAdd ? { addGroupId: groupId } : { removeGroupId: groupId };
  const res = await axios.patch(`stories/${story._id}/edit`, params);
  dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
  return isAdd;
};

export const exportToPermanentOrg = (storyId) => async (dispatch) => {
  await axios.post(`/user/permanentExport/${storyId}`);
  const res = await axios.get(`/stories/${storyId}`);
  dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
};

export const transcribe = (storyId, languageCode, regenerate) => async (dispatch) => {
  await axios.post(`/stories/${storyId}/transcribe`, { language_code: languageCode, regenerate: regenerate });
  const res = await axios.get(`/stories/${storyId}`);
  dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
};

export const transcribeModeAdvanced = (storyId, language, regenerate, deepgramTranscription) => async (dispatch) => {
  let transcriptMethod = deepgramTranscription ? "deepgram_transcribe" : "advanced_transcribe";
  if (regenerate) {
    transcriptMethod = "advanced_transcribe";
    await axios.post(`/transcripts/${storyId}/saveversion`);
  }
  await axios.post(`/stories/${storyId}/${transcriptMethod}`, { language: language, regenerate: regenerate });
  const res = await axios.get(`/stories/${storyId}`);
  dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
};

export const downloadLocalVideos =
  (storyId: string): ActionTypeHelper<Promise<string>> =>
  async (dispatch, getState) => {
    const sdk = getTheirStorySdk(dispatch, getState);

    const res = await sdk.getStoryAsyncTaskCount({
      storyId,
      type: "TranscodeLocalVideo",
      status: "Pending",
    });
    if (res.story.asyncTaskCount) {
      return Promise.resolve("<Pending>");
    }
    const response = await axios.get(`/stories/${storyId}/download-all-local-videos`);
    const taskId = response.data.buildDownloadTaskId;

    return new Promise((resolve, reject) => {
      sdk
        .watchAsyncTask({ taskId })
        .pipe(
          skipWhile((x) => x.asyncTaskUpdated?.status === "Pending"),
          take(1)
        )
        .subscribe({
          next: (value) => {
            if (value.asyncTaskUpdated.__typename !== "BuildDownloadTask") {
              reject(new Error(`Unexpected task type: ${value.asyncTaskUpdated.__typename}`));
            } else if (!value.asyncTaskUpdated) {
              reject(new Error("Task failed"));
            } else if (value.asyncTaskUpdated.status === "Completed") {
              resolve(value.asyncTaskUpdated.downloadUrl);
            } else if (value.asyncTaskUpdated.status === "Failed") {
              reject(new Error(`Task failed: '${value.asyncTaskUpdated.errorMsg}'`));
            }
          },
          error: reject,
        });
    });
  };

export interface StoryTaskUpdated {
  id: AsyncTaskId;
  storyId: StoryId;
  taskType: AsyncTaskType;
  status: AsyncTaskStatus;
}

export const storyTaskUpdated = createAction<StoryTaskUpdated>(STORY_TASK_UPDATED);

export const clearStories = createAction(STORIES_CLEAR);
